import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { AccountingSyncBadge } from '@/components/AccountingSyncBadge'
import { OwnedChip } from '@/components/OwnedChip'
import { ContactPaymentsHistory } from '@/pages/ContactPaymentsHistory'
import { ContactTrainingPurchased } from '@/pages/ContactTrainingPurchased'
import { DataTableBlock, type ListPageColumn } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  Contact,
  ContactFinancialInvoice,
  ContactFinancialRefund,
  ContactFinancialTransactions,
} from '@/lib/contacts/types'
import { postJSON } from '@/lib/api'
import { formatMoneyCents } from '@/lib/money'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { displayDate } from '@/lib/dates'
import { invoiceStatusVariant } from '@/pages/InvoicesPage.status'
import type { Invoice } from '@/pages/InvoicesPage.types'

interface ContactLike {
  id: number
  name: string
  email?: string | null
  contact_type: Contact['contact_type']
  archived?: boolean
}

const invoiceColumns: ListPageColumn<ContactFinancialInvoice>[] = [
  {
    id: 'number',
    header: 'Invoice',
    cell: (invoice) => (
      <div className="flex flex-col">
        <span className="flex items-center gap-2 font-medium text-foreground">
          #
          {String(
            'source_invoice_number' in invoice ? invoice.source_invoice_number : invoice.number,
          )}
          <OwnedChip owner={invoice.owner} />
        </span>
        <span className="text-caption text-muted-foreground">{invoice.origin}</span>
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (invoice) => (
      <Badge variant={invoiceStatusVariant[invoice.status]}>{invoice.status}</Badge>
    ),
  },
  {
    id: 'due',
    header: 'Due',
    cell: (invoice) => <span className="text-muted-foreground">{displayDate(invoice.due_on)}</span>,
  },
  {
    id: 'accounting',
    header: 'Accounting',
    cell: (invoice) => (
      <AccountingSyncBadge
        state={invoice.accounting_sync_state ?? null}
        detail={invoice.accounting_sync_detail}
      />
    ),
  },
  {
    id: 'total',
    header: 'Total',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (invoice) => invoice.total,
  },
  {
    id: 'balance',
    header: 'Balance',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (invoice) => invoice.balance,
  },
]

export function ContactFinancialTransactionsSection({
  contact,
  transactions,
  onOpenInvoice,
  onRefundRecorded,
}: {
  contact: ContactLike
  transactions: ContactFinancialTransactions
  onOpenInvoice?: (invoice: Invoice) => void
  onRefundRecorded?: () => void | Promise<unknown>
}) {
  const [refundDialogOpen, setRefundDialogOpen] = React.useState(false)
  useFinancialTransactionHash(transactions)
  const refund = useMutation({
    mutationFn: (form: ManualRefundForm) => submitManualRefund(contact.id, form),
    onSuccess: async () => {
      setRefundDialogOpen(false)
      await onRefundRecorded?.()
    },
  })

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-0">
          <CardTitle className="text-body font-semibold">Account Credit</CardTitle>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={transactions.account_credit_cents === 0}
            onClick={() => setRefundDialogOpen(true)}
          >
            Record Refund
          </Button>
        </CardHeader>
        <CardContent className="p-4">
          <p className="text-heading-sm font-semibold text-foreground tabular-nums">
            {transactions.account_credit}
          </p>
        </CardContent>
      </Card>

      <ContactTrainingPurchased purchases={transactions.training_purchases} />
      <TransactionsInvoicesTable
        contact={contact}
        invoices={transactions.invoices}
        onOpenInvoice={onOpenInvoice}
      />
      <ContactPaymentsHistory payments={transactions.payments} />
      <TransactionsRefundsTable refunds={transactions.refunds ?? []} />
      {refundDialogOpen && (
        <ManualRefundDialog
          accountCredit={transactions.account_credit}
          pending={refund.isPending}
          error={refund.error}
          onClose={() => setRefundDialogOpen(false)}
          onSubmit={(form) => refund.mutate(form)}
        />
      )}
    </div>
  )
}

export function emptyFinancialTransactions(): ContactFinancialTransactions {
  return {
    account_credit: formatMoneyCents(0, 'USD'),
    account_credit_cents: 0,
    currency: 'USD',
    invoices: [],
    payments: [],
    refunds: [],
  }
}

export function initialFinancialTransactionsTab() {
  return /^#(?:payment|refund)-\d+$/.test(window.location.hash) ? 'transactions' : 'profile'
}

function TransactionsRefundsTable({ refunds }: { refunds: ContactFinancialRefund[] }) {
  return (
    <section className="space-y-3">
      <h3 className="text-body font-semibold text-foreground">Refunds</h3>
      {refunds.length === 0 ? (
        <EmptyState title="No Refunds recorded" className="items-start px-0 text-left" />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Refund</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {refunds.map((refund) => (
                <TableRow id={`refund-${refund.id}`} key={refund.id}>
                  <TableCell>
                    <span className="font-medium text-foreground">{refund.amount}</span>
                    <span className="block text-caption text-muted-foreground">
                      {displayDate(refund.recorded_at.slice(0, 10))}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{refund.method}</TableCell>
                  <TableCell className="text-muted-foreground">{refund.reference || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function useFinancialTransactionHash(transactions: ContactFinancialTransactions) {
  React.useEffect(() => {
    const target = document.getElementById(window.location.hash.slice(1))
    target?.scrollIntoView({ block: 'center' })
  }, [transactions])
}

interface ManualRefundForm {
  amount: string
  reference: string
  idempotencyKey: string
}

function ManualRefundDialog({
  accountCredit,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  accountCredit: string
  pending: boolean
  error: Error | null
  onClose: () => void
  onSubmit: (form: ManualRefundForm) => void
}) {
  const [form, setForm] = React.useState<ManualRefundForm>(() => ({
    amount: '',
    reference: '',
    idempotencyKey: refundIdempotencyKey(),
  }))
  const amountError = apiFieldMessages(error, 'amount')[0]

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <form
          onSubmit={(event) => {
            event.preventDefault()
            onSubmit(form)
          }}
        >
          <DialogHeader>
            <DialogTitle>Record manual Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-body-sm text-muted-foreground">
              Record money returned outside the platform. Available Account Credit: {accountCredit}.
            </p>
            {error && (
              <Alert variant="destructive">{amountError ?? 'Refund could not be recorded'}</Alert>
            )}
            <FormField label="Amount" htmlFor="refund-amount" required error={amountError}>
              <Input
                id="refund-amount"
                inputMode="decimal"
                value={form.amount}
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
              />
            </FormField>
            <FormField label="Reference" htmlFor="refund-reference">
              <Input
                id="refund-reference"
                value={form.reference}
                onChange={(event) => setForm({ ...form, reference: event.target.value })}
              />
            </FormField>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Recording...' : 'Record Refund'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function submitManualRefund(contactId: number, form: ManualRefundForm) {
  return postJSON(`/api/v1/contacts/${contactId}/refunds`, {
    refund: {
      amount: form.amount,
      reference: form.reference,
      idempotency_key: form.idempotencyKey,
    },
  })
}

function refundIdempotencyKey() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()

  return `refund-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function TransactionsInvoicesTable({
  contact,
  invoices,
  onOpenInvoice,
}: {
  contact: ContactLike
  invoices: ContactFinancialInvoice[]
  onOpenInvoice?: (invoice: Invoice) => void
}) {
  const openInvoice = onOpenInvoice
    ? (invoice: ContactFinancialInvoice) => onOpenInvoice(profileInvoice(contact, invoice))
    : undefined

  return (
    <section className="space-y-3">
      <h3 className="text-body font-semibold text-foreground">Invoices</h3>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <DataTableBlock
          table={{
            rows: invoices,
            columns: invoiceColumns,
            getRowId: (invoice) => invoice.id,
            getRowLabel: (invoice) => `Invoice #${invoice.number}`,
            onRowOpen: openInvoice,
            emptyTitle: 'No Invoices recorded',
          }}
        />
      </div>
    </section>
  )
}

function profileInvoice(contact: ContactLike, invoice: ContactFinancialInvoice): Invoice {
  return {
    ...invoice,
    contact_id: contact.id,
    contact_name: contact.name,
    contact: {
      id: contact.id,
      name: contact.name,
      email: contact.email ?? null,
      contact_type: contact.contact_type,
      archived: contact.archived ?? false,
    },
    payment_link_available: false,
    payment_link_url: null,
    payment_link_unavailable_reason: null,
    phone_payment_available: false,
    phone_payment_unavailable_reason: null,
    line_items: [],
    history: [],
  }
}
